/**
 * Set Cleaning Standards Page (Step 3)
 * Allows users to define cleaning standards for facilities and locations.
 * Clean code: single responsibility, composed hooks, presentational components.
 * SOLID: page orchestrates only; logic in hooks and constants.
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ROUTES from '../../constants/routes';
import { WizardActionBar } from '../../components/organizationWizard/WizardActionBar';
import { AddFacilityModal } from '../../components/organizationFacilities/AddFacilityModal';
import { FacilitiesSearchList } from '../../components/organizationFacilities/FacilitiesSearchList';
import { useFeedbackContext } from '../../context';
import MainPageLayout from '../../components/common/MainPageLayout';
import {
  useCleaningStandardsFacilities,
  useFacilitySelection,
  useCleaningStandardsModals,
  useCleaningStandardsActions,
} from './hooks';
import {
  PageHeader,
  FacilitiesEmptyState,
  FacilityStandardsSection,
  LocationsStandardsSection,
} from './components';
import { WIZARD_STEP, PANEL_CARD_CLASS } from './constants';

function getOrganizationId(state) {
  return state?.auth?.user?.organization || localStorage.getItem('organizationId');
}

export function SetCleaningStandardsPage() {
  const navigate = useNavigate();
  const { showToast } = useFeedbackContext();
  const organizationId = useSelector(getOrganizationId);

  const { facilitiesFromList, handleFacilitiesChange, queryScope } =
    useCleaningStandardsFacilities();

  const {
    selectedFacilityIds,
    handleSelectFacility,
    handleSelectAll,
    selectedFacilityNames,
    isSingleFacilitySelected,
  } = useFacilitySelection(facilitiesFromList);

  const { isAddModalOpen, isEditModalOpen, facilityToEdit, closeAddModal, closeEditModal } =
    useCleaningStandardsModals();

  const { handleAddFacility, handleUpdateFacility } = useCleaningStandardsActions(showToast);

  const handleContinue = useCallback(() => {
    navigate(ROUTES.ORGANIZATION_CONFIGURATION);
  }, [navigate]);

  const handleUpdateFacilityAndClose = useCallback(
    async (data) => {
      if (!facilityToEdit) return;
      await handleUpdateFacility(facilityToEdit.id, data);
      closeEditModal();
    },
    [facilityToEdit, handleUpdateFacility, closeEditModal],
  );

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <div className="w-full">
        <div className="max-w-[1400px] mx-auto px-6 pt-4 pb-32">
          <PageHeader />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,360px)_1fr] gap-6 lg:gap-8 mb-8">
            <div className={`${PANEL_CARD_CLASS} p-6`}>
              <FacilitiesSearchList
                variant="infinite"
                queryScope={queryScope}
                organizationId={organizationId}
                selectedFacilityIds={selectedFacilityIds}
                onSelectFacility={handleSelectFacility}
                onSelectAll={handleSelectAll}
                onFacilitiesChange={handleFacilitiesChange}
                emptyState={<FacilitiesEmptyState />}
              />
            </div>

            <div className="space-y-6">
              <FacilityStandardsSection
                facilityIds={selectedFacilityIds}
                facilityNames={selectedFacilityNames}
                onStandardsChange={() => {}}
              />
              <LocationsStandardsSection
                organizationId={organizationId}
                queryScope={queryScope}
                selectedFacilityIds={selectedFacilityIds}
                isSingleFacilitySelected={isSingleFacilitySelected}
              />
            </div>
          </div>

          <WizardActionBar
            currentStep={WIZARD_STEP.CURRENT}
            totalSteps={WIZARD_STEP.TOTAL}
            onContinue={handleContinue}
            stepName={WIZARD_STEP.NAME}
            continueText="Back to overview"
          />
        </div>

        <AddFacilityModal
          isOpen={isAddModalOpen}
          onClose={closeAddModal}
          onSubmit={handleAddFacility}
          organizationId={organizationId}
        />

        {facilityToEdit && (
          <AddFacilityModal
            isOpen={isEditModalOpen}
            onClose={closeEditModal}
            onSubmit={handleUpdateFacilityAndClose}
            organizationId={organizationId}
            editMode={true}
            initialData={facilityToEdit}
          />
        )}
      </div>
    </MainPageLayout>
  );
}
